/**
 * ClientsSection — план и факт по обращениям.
 *
 * Сначала показываем, откуда взялся план, и только потом — насколько он
 * не выполнен. Большинство читателей плана по обращениям не задавали: он
 * выводится из бюджета и плановой цены. Не сказав этого, мы предъявляем
 * цифру, которая выглядит выдуманной.
 *
 * Формула стоит одной строкой из трёх облачков — бюджет разделить на цену
 * обращения даёт их количество. Ранняя версия рисовала это тремя
 * карточками с иконками и знаком умножения: и тяжелее, и арифметически
 * неверно.
 *
 * @format
 */

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';

import {Button} from 'ui/components/atoms/Button';
import {Row} from 'ui/components/layout/Row';
import {Stack} from 'ui/components/layout/Stack';
import {spacingPx} from 'ui/components/layout/spacing';
import {text} from 'ui/theme/typography';
import {surfaceVar} from 'ui/theme/surfaceContext';

import {floorLabel} from '../floors';
import {useReportFormat} from '../format';
import {ENQUIRIES, MEASURE, count, plural, pluralize, TABULAR} from '../helpers';
import type {Clients, SectionId} from '../types';
import {Metric} from './Metric';
import {MeterBar} from './MeterBar';
import {ReportCard} from './ReportCard';

export interface ClientsSectionProps {
  data: Clients;
  onNavigate: (id: SectionId) => void;
}

/**
 * Формула набирается без плашек: это одно предложение, записанное цифрами.
 * Каждая подложка под членом формулы дробила бы его на три независимых
 * блока, а связывает их как раз то, что они стоят подряд.
 */
const Operator = styled(Box)({
  ...text('heading.l'),
  color: surfaceVar('onSurfaceMuted'),
  flex: 'none',
  alignSelf: 'center',
  paddingTop: spacingPx('m'),
});

const Explain = styled(Box)({
  ...text('body.l'),
  color: surfaceVar('onSurfaceLight'),
  maxWidth: MEASURE,
});

/** Подпись полученного — над полосой, слева. */
const BarLabel = styled(Box)({
  ...text('label.l'),
  ...TABULAR,
  color: surfaceVar('onSurface'),
});

/** Подпись недобора — та же строка, справа. */
const BarMissing = styled(Box)(({theme}) => ({
  ...text('label.l'),
  ...TABULAR,
  color: theme.palette.bad.main,
}));

const Deficit = styled(Box)(({theme}) => ({
  ...text('heading.m'),
  color: theme.palette.bad.main,
  maxWidth: MEASURE,
}));

const Caption = styled(Box)({
  ...text('body.l'),
  color: surfaceVar('onSurfaceLight'),
  maxWidth: MEASURE,
});

export function ClientsSection({data, onNavigate}: ClientsSectionProps) {
  const fmt = useReportFormat();
  // Недобор считает бэкенд: у него есть и план, и факт, и правило, что
  // делать при перевыполнении. Доля — уже наше дело.
  const plan = data.leadsPlan ?? 0;
  const deficit = data.gap ?? Math.max(0, plan - data.leadsFact);
  const deficitPct = plan > 0 ? Math.round((deficit / plan) * 100) : 0;

  return (
    <ReportCard padding='l' clip>
      <Stack gap='l'>
        <Explain>
          {data.planSource === 'MEDIAPLAN'
            ? 'План по обращениям взят из вашего медиаплана.'
            : 'Плана по обращениям в медиаплане нет, поэтому мы считаем его сами: израсходованный бюджет делим на вашу плановую цену обращения.'}
        </Explain>

        {/* Бюджет ÷ цена обращения = количество обращений. */}
        <Row gap='l' alignY='start' wrap='wrap'>
          <Metric label='Израсходованный бюджет' value={fmt.money(data.budget)} size='m' />
          <Operator>÷</Operator>
          <Metric label='Плановая цена обращения' value={fmt.money(data.cplPlan ?? 0)} size='m' />
          <Operator>=</Operator>
          <Metric label='План по обращениям' value={plan} />
        </Row>

        {/* Полоса идёт впереди вывода: сначала читатель видит пропорцию,
            потом читает, как она называется. Обратный порядок заставлял
            искать глазами, к чему относится «недобор 16 обращений».

            Подписи стоят НАД полосой, а не внутри неё: в заливку они
            помещались, только пока доля широкая, а при выполнении плана на
            два процента их приходилось выталкивать наружу. */}
        <Stack gap='xs'>
          <Row gap='m' justify='between' alignY='baseline' wrap='wrap'>
            <BarLabel>
              {count(data.leadsFact)} {plural(data.leadsFact, ENQUIRIES)} из {count(plan)} получено
            </BarLabel>
            {deficit > 0 && <BarMissing>{count(deficit)} не получено</BarMissing>}
          </Row>
          <MeterBar value={plan > 0 ? data.leadsFact / plan : 0} tone='primary' height={20} />
        </Stack>

        {/* Вывод идёт ПОД шкалой: сперва видна пропорция, следом её
            название и цена. */}
        <Stack gap='xxs'>
          <Deficit>
            Недобор {pluralize(deficit, ENQUIRIES)} — это {deficitPct}% от плана
          </Deficit>
          <Caption>
            Обращение обошлось в {fmt.money(data.cplFact ?? 0)} вместо плановых{' '}
            {fmt.money(data.cplPlan ?? 0)}.
          </Caption>
        </Stack>

        {/* Кнопка не выполняет действие, а перемещает — иконка и адрес
            этажа говорят об этом до клика. */}
        <Row gap='s' justify='start'>
          <Button
            size='m'
            variant='outline'
            color='primary'
            rightIcon='arrow_downward'
            onClick={() => onNavigate('tasks')}
          >
            Что делать (в блоке «{floorLabel('tasks')}»)
          </Button>
        </Row>
      </Stack>
    </ReportCard>
  );
}
